/**
 * React-query bindings for the cross-domain staff endpoints (inventory,
 * service catalog, notifications). Kept separate from useWorkshop.ts so
 * the work-order hooks stay focused.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { staffApi, type ReorderRequestBody } from '@/apis/staffApis'
import { FEATURE_TECH_PART_REQUESTS } from '@/featureFlags'
import type {
  CreatePartRequestBody,
  PartRequestResponse,
  StockLevelResponse,
} from '@/apis/types'

const partsKey = ['staff', 'catalog-parts'] as const
const servicesKey = ['staff', 'catalog-services'] as const
const stockKey = (partId: string) => ['staff', 'stock', partId] as const
const notificationsKey = ['staff', 'notifications'] as const

export function useCatalogParts() {
  return useQuery({
    queryKey: partsKey,
    queryFn: () => staffApi.listParts(),
    staleTime: 60_000,
  })
}

/** Submit a material-reorder request to management. Refreshes the catalog
 *  parts cache on success so any newly-created materials show up. */
export function useSubmitReorder() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, ReorderRequestBody>({
    mutationFn: (body) => staffApi.submitReorder(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: partsKey }),
  })
}

export function useCatalogServices() {
  return useQuery({
    queryKey: servicesKey,
    queryFn: () => staffApi.listServices(),
    staleTime: 60_000,
  })
}

/** Stock for one part. Returns `null` (never stocked) without erroring. */
export function useStock(partId: string | undefined) {
  return useQuery<StockLevelResponse | null>({
    queryKey: stockKey(partId ?? ''),
    queryFn: () => staffApi.getStock(partId!),
    enabled: Boolean(partId),
    staleTime: 30_000,
  })
}

export function useNotifications() {
  return useQuery({
    queryKey: notificationsKey,
    queryFn: () => staffApi.listNotifications(),
    staleTime: 30_000,
  })
}

/**
 * Dismiss one notification. Only rows assigned to this technician can be
 * dismissed; the backend 403s the unassigned shop-wide ones the bell also
 * shows, so the UI hides the button for those rather than offering an
 * action that fails.
 */
export function useDismissNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => staffApi.dismissNotification(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKey })
    },
  })
}

/** Dismiss everything assigned to this technician — one request, not one per row. */
export function useDismissAllNotifications() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => staffApi.dismissAllNotifications(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKey })
    },
  })
}

const partRequestsKey = (workOrderId: string) => ['part-requests', workOrderId] as const

/**
 * Every part request on a work order. The workspace groups them by task so
 * a technician can see what they already asked for and where it stands —
 * without this a filed request left no trace anywhere in their app.
 */
export function usePartRequests(workOrderId: string | undefined) {
  return useQuery({
    queryKey: partRequestsKey(workOrderId ?? ''),
    // Technicians no longer file part requests, and the backend gates
    // TechnicalPartRequestController behind app.features.partRequests — so
    // without this the WO detail would 404 on the app-wide 30s poll
    // (App.tsx `refetchInterval`) for as long as the screen is open, which
    // on a running task is all day.
    enabled: FEATURE_TECH_PART_REQUESTS && !!workOrderId,
    queryFn: () => staffApi.listPartRequests(workOrderId as string),
    staleTime: 30_000,
  })
}

/** File a single part request — the only way a technician names a WO part. */
export function useCreatePartRequest() {
  const qc = useQueryClient()
  return useMutation<PartRequestResponse, Error, CreatePartRequestBody>({
    mutationFn: (body) => staffApi.createPartRequest(body),
    // Refresh the list the workspace reads, so the new request appears
    // under its task instead of vanishing until a reload.
    onSuccess: (_res, body) => {
      if (body.workOrderId) {
        qc.invalidateQueries({ queryKey: partRequestsKey(body.workOrderId) })
      }
    },
  })
}

/** Take back one's own pending request. */
export function useWithdrawPartRequest(workOrderId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<PartRequestResponse, Error, string>({
    mutationFn: (id) => staffApi.withdrawPartRequest(id),
    onSuccess: () => {
      if (workOrderId) {
        qc.invalidateQueries({ queryKey: partRequestsKey(workOrderId) })
      }
    },
  })
}
